class LuxProductGallery extends HTMLElement {
    constructor() {
        super();
        this.mobileBreakpoint = 1024;
        this.currentIndex = 0;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.touchStartY = 0; 
        this.touchEndY = 0;   
        this.currentZoom = 1;
        this.zoomStep = .25;
        this.zoomMin = .25;
        this.zoomMax = 2.5;
        this.galleryInitialized = false;
        this.isTransitioning = false; // Prevents click-spamming during snap
    }

    connectedCallback() {
        this.slides = this.querySelectorAll('.lux-product-gallery__frame');
        this.galleryTrack = this.querySelector('.lux-product-gallery__items');
        
        // SELECT UI Elements
        this.thumbGallery = this.querySelector('.lux-product-gallery__thumb');
        this.thumbModal = this.querySelector('.lux-product-modal__thumb');
        this.prevArrow = this.querySelector('.lux-product-gallery__arrow.prev');
        this.nextArrow = this.querySelector('.lux-product-gallery__arrow.next');

        this.modal = this.querySelector('.lux-product-modal');
        this.modalClose = this.querySelector('.lux-product-modal__close');
        this.modalTrack = this.querySelector('.lux-product-modal__items');
        this.modalThumbs = this.querySelector('.lux-product-modal__thumbs');
        this.modalCards = this.querySelector('.lux-product-modal__cards');
        this.modalCardItems = this.querySelectorAll('.lux-product-modal__card');

        this.modalZoomIn = this.querySelector('.lux-product-modal__zoom-in');
        this.modalZoomOut = this.querySelector('.lux-product-modal__zoom-out');

        if(this.modalZoomIn) this.modalZoomIn.addEventListener('click', () => this.handleZoom(+1));
        if(this.modalZoomOut) this.modalZoomOut.addEventListener('click', () => this.handleZoom(-1));

        if(this.slides.length) {
            // 1. Create Ghost Clones for seamless infinite forward
            this.createClones();

            // 2. Initialize modal (Desktop & Mobile)
            this.initModal();
            
            // 3. Initialize sliding gallery if on mobile
            if (this.isMobile()) {
                this.initGallery();
            }
        }

        window.addEventListener('resize', () => {
            if(this.slides.length && this.isMobile()) {
                requestAnimationFrame(() => this.updateUI());
            }

            if(this.slides.length) {
                this.initializeGaps('gallery', this.slides, this.querySelector('.lux-product-gallery__gaps'));
                this.initializeThumbs('gallery', this.slides, this.thumbGallery);
            }
        });
    }
    createClones() {
        // 1. MAIN GALLERY CLONE (Includes Badges & Certificates)
        const firstGallerySlide = this.querySelector('.lux-product-gallery__frame');
        const galleryTrack = this.querySelector('.lux-product-gallery__items');
        
        if (galleryTrack && firstGallerySlide) {
            // Create a deep copy of the first slide
            const galleryClone = firstGallerySlide.cloneNode(true);
            galleryClone.classList.add('is-clone');

            // Select the specific badges/certificates inside the main track
            // These are the ones that currently scroll with slide 1
            const bestseller = galleryTrack.querySelector('.lux-gallery-bestseller');
            const certs = galleryTrack.querySelector('.lux-product-certificates');
            const badgesInsideTrack = galleryTrack.querySelector('.lux-product-card__badges');

            // Move the copies INTO the cloned frame so they stay attached to it
            if (bestseller) galleryClone.appendChild(bestseller.cloneNode(true));
            if (certs) galleryClone.appendChild(certs.cloneNode(true));
            if (badgesInsideTrack) galleryClone.appendChild(badgesInsideTrack.cloneNode(true));

            // Add the fully equipped clone to the end of the track
            galleryTrack.appendChild(galleryClone);
        }

        // 2. MODAL CLONE (Clean Image Only)
        const firstModalSlide = this.querySelector('.lux-product-modal__frame');
        if (this.modalTrack && firstModalSlide) {
            const modalClone = firstModalSlide.cloneNode(true);
            modalClone.classList.add('is-clone');

            // We do NOT append badges here. 
            // This ensures the modal remains an "image-only" viewing experience.
            this.modalTrack.appendChild(modalClone);
        }
    }

    initGallery() {
        if (this.galleryInitialized) return;

        this.initializeGaps('gallery', this.slides, this.querySelector('.lux-product-gallery__gaps'));
        this.initializeThumbs('gallery', this.slides, this.thumbGallery);

        this.galleryTrack?.addEventListener('touchstart', (e) => {
            if(!this.isMobile()) return;
            this.touchStartX = e.changedTouches[0].clientX;
            this.touchStartY = e.changedTouches[0].clientY;
        }, { passive: true });

        this.galleryTrack?.addEventListener('touchend', (e) => {
            if(!this.isMobile()) return;
            this.touchEndX = e.changedTouches[0].clientX;
            this.touchEndY = e.changedTouches[0].clientY;
            this.handleSwipe();
        }, { passive: true });

        this.prevArrow?.addEventListener('click', () => this.slideStep(-1));
        this.nextArrow?.addEventListener('click', () => this.slideStep(1));

        this.galleryInitialized = true;
        this.updateUI();
    }

    initModal() {
        const gapsModal = this.querySelector('.lux-product-modal__gaps');
        if(gapsModal) this.initializeGaps('modal', this.slides, gapsModal);
        if(this.thumbModal) this.initializeThumbs('modal', this.slides, this.thumbModal);

        if (this.modalThumbs && this.modalCards) {
            const resizeObserver = new ResizeObserver(() => {
                const scrollWidth = this.modalCards.scrollWidth;
                if (scrollWidth > 0) {
                    this.modalThumbs.style.setProperty('--size', `${scrollWidth}px`);
                    this.initializeThumbs('modal', this.slides, this.thumbModal);
                    this.updateProgressBar(this.thumbModal);
                }
            });
            resizeObserver.observe(this.modalCards);
        }

        this.slides.forEach((slide, index) => {
            slide.addEventListener('click', () => this.openModal(index));
        });

        if(this.modal && this.modalClose) {
            this.modalClose.addEventListener('click', () => {
                this.modal.setAttribute('aria-expanded', false)
                this.resetZoom();
            });
        }

        const modalPrev = this.querySelector('.lux-product-modal__arrow.prev');
        const modalNext = this.querySelector('.lux-product-modal__arrow.next');
        modalPrev?.addEventListener('click', () => this.slideStep(-1));
        modalNext?.addEventListener('click', () => this.slideStep(1));

        this.modalTrack?.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].clientX;
            this.touchStartY = e.changedTouches[0].clientY;
        }, { passive: true });

        this.modalTrack?.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].clientX;
            this.touchEndY = e.changedTouches[0].clientY;
            this.handleSwipe();
        }, { passive: true });

        this.modalCardItems.forEach((card, index) => {
            card.addEventListener('click', () => {
                if(this.isTransitioning) return;
                this.currentIndex = index;
                this.updateUI();
            });
        });

        this.updateUI();
    }

    openModal(index) {
        if(!this.modal) return;
        this.currentIndex = index;
        this.modal.setAttribute('aria-expanded', true);
        this.updateUI();
    }

    handleSwipe() {
        const swipeThreshold = 40; 
        const xDiff = this.touchStartX - this.touchEndX;
        const yDiff = this.touchStartY - this.touchEndY;

        if (Math.abs(xDiff) > Math.abs(yDiff) && Math.abs(xDiff) > swipeThreshold) {
            if (xDiff > 0) {
                this.slideStep(1);
            } else {
                this.slideStep(-1);
            }
            this.resetTouch();
        }
    }

    resetTouch() {
        this.touchStartX = this.touchEndX = this.touchStartY = this.touchEndY = 0;
    }

    slideStep(direction) {
        if (this.isTransitioning) return;

        const newIndex = this.currentIndex + direction;

        // Hard stop at the beginning (no reverse loop)
        if (newIndex < 0) return;

        this.currentIndex = newIndex;
        this.updateUI();

        // Infinite Forward Logic: Check if we landed on the clone
        if (this.currentIndex === this.slides.length) {
            this.handleInfiniteReset();
        }
    }

    handleInfiniteReset() {
        this.isTransitioning = true;
        
        // Wait for the 0.4s transition to land on the clone
        setTimeout(() => {
            this.currentIndex = 0;

            const tracks = [this.galleryTrack, this.modalTrack];
            // Kill transition for the jump back to the start
            tracks.forEach(t => { if(t) t.style.transition = 'none'; });

            this.updateUI();

            // Force reflow and restore transition
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    tracks.forEach(t => { if(t) t.style.transition = ''; });
                    this.isTransitioning = false;
                });
            });
        }, 400); 
    }

    updateUI() {
        this.resetZoom();
        
        const realCount = this.slides.length; 
        const displayIndex = this.currentIndex % realCount;

        // 1. Frame Active State
        this.querySelectorAll('.lux-product-modal__frame').forEach((slide, index) => {
            slide.classList.toggle('is-active', index === displayIndex);
        });

        // 2. Translate Tracks (currentIndex can be realCount if on clone)
        if (this.galleryTrack && this.isMobile()) {
            const slideWidth = this.slides[0]?.offsetWidth || 0;
            this.galleryTrack.style.transform = `translateX(-${this.currentIndex * slideWidth}px)`;
        }

        if (this.modalTrack) {
            this.modalTrack.style.transform = `translateX(${this.currentIndex * -100}%)`;
        }

        // 3. Update Markers (using displayIndex to keep them synced)
        this.updateProgressBar(this.thumbGallery, displayIndex);
        this.updateProgressBar(this.thumbModal, displayIndex);

        if (this.modalCardItems.length > 0) {
            this.modalCardItems.forEach((card, index) => {
                card.classList.toggle('is-active', index === displayIndex);
            });
            // Only auto-scroll cards if not in the middle of a reset
            if(!this.isTransitioning) {
                this.modalCardItems[displayIndex].scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest', 
                    inline: 'center' 
                });
            }
        }

        // 4. Update Arrows
        const isFirst = this.currentIndex === 0;
        this.querySelectorAll('.prev').forEach(btn => btn.toggleAttribute('disabled', isFirst));
        this.querySelectorAll('.next').forEach(btn => btn.removeAttribute('disabled'));
    }

    updateProgressBar(thumbElement, index = this.currentIndex) {
        if (!thumbElement) return;
        const parentWidth = thumbElement.parentElement.offsetWidth;
        if (parentWidth === 0) return; 

        const thumbWidth = parentWidth / this.slides.length;
        thumbElement.style.setProperty('--size', `${thumbWidth}px`); 
        thumbElement.style.setProperty('--offset', `${index * thumbWidth}px`);
    }

    handleZoom(modifier) {
        const target = this.querySelector('.lux-product-modal__frame.is-active .lux-product-modal__image');
        if (!target) return;
        this.currentZoom += this.zoomStep * modifier;
        this.currentZoom = Math.min(Math.max(this.currentZoom, this.zoomMin), this.zoomMax);
        target.style.scale = this.currentZoom;
    }

    resetZoom() {
        this.currentZoom = 1;
        this.querySelectorAll('.lux-product-modal__image').forEach(img => img.style.scale = 1);
    }

    isMobile() { return window.innerWidth < this.mobileBreakpoint; }

    initializeGaps(component, slides, element) {
        if(!slides.length || !element) return;
        const identifier = component === 'gallery' ? 'lux-product-gallery__gap' : 'lux-product-modal__gap';
        const iterations = slides.length - 1;
        element.innerHTML = ''; 
        for(let i = 0; i < iterations; i++) {
            const gap = document.createElement('div');
            gap.classList.add(identifier);
            element.appendChild(gap);
        }
    }

    initializeThumbs(component, slides, element) {
        if(!element) return;
        const parent = element.parentElement;
        const containerWidth = parent ? parent.offsetWidth : this.offsetWidth;
        const size = containerWidth / slides.length;
        if(size) element.style.setProperty('--size', `${size}px`);
    }
}

if(!customElements.get('lux-product-gallery')) customElements.define('lux-product-gallery', LuxProductGallery);